import React, { useEffect, createContext } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

// Import the 409 error flag from axiosInterceptor
import { getIs409ErrorActive } from '../../services/axiosInterceptor';
// Context to signal Layout to hide header/footer
export const HideLayoutContext = createContext(false);

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const spinnerStyle: React.CSSProperties = {
  display: 'inline-block',
  width: 32,
  height: 32,
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #3498db',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginRight: 12,
};

const spinnerKeyframes = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  console.info('ProtectedRoute mounted');
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const returnTo = location.state?.from?.pathname || location.pathname;

  const [showReload, setShowReload] = React.useState(false);

  useEffect(() => {
    let timeout: any;
    if (isLoading) {
      timeout = setTimeout(() => {
        setShowReload(true);
      }, 15000); // 15 seconds
    } else {
      setShowReload(false);
    }
    return () => clearTimeout(timeout);
  }, [isLoading]);

  // Always call hooks first
  useEffect(() => {
    const isLoggingOut = sessionStorage.getItem('is_logging_out') === 'true';

    if (isAuthenticated) {
      sessionStorage.removeItem('is_logging_out');
    }

    if (!isAuthenticated && !isLoading && !isLoggingOut) {
      console.warn('ProtectedRoute - User not authenticated, redirecting to login');
      loginWithRedirect({
        appState: { returnTo },
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, returnTo]);

  // Import the global 409 error flag (must be kept in sync with axiosInterceptor)
  if (getIs409ErrorActive()) {
    return <Navigate to="/error-mismatched-login" replace />;
  }

  const navType = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type;
  const cameFromInside = location.state?.cameFromInside === true;
  const isDirectAccess = (navType === "navigate" || navType === "reload") && location.pathname === "/fastrack" && !cameFromInside;

  console.log('ProtectedRoute - Auth State:', { isAuthenticated, isLoading, path: location.pathname });

  if (isDirectAccess) {
    console.warn("BLOCKED: Direct access to /fastrack, redirecting...");
    return <Navigate to="/search" replace />;
  }

  if (isLoading || !isAuthenticated) {
    // Just return the spinner, context will be set by App
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <style>{spinnerKeyframes}</style>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
            <span style={spinnerStyle}></span>
            <span style={{ fontSize: 18, color: '#555' }}>Loading...</span>
          </div>
          {showReload && (
            <div style={{ textAlign: 'center', animation: 'fadeIn 0.5s ease-in' }}>
              <p style={{ color: '#666', marginBottom: 15 }}>Taking longer than expected?</p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#0056D2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Reload Page
              </button>
            </div>
          )}
        </div>
        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </div>
    );
  }

  console.info('ProtectedRoute - User authenticated, rendering protected content');
  return <>{children}</>;
};

export default ProtectedRoute;